import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ setIsAuthenticated, setUser }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    name: '',
    plan: 'free_trial'  // Default: teste grátis
  });

  const plans = [
    { 
      id: 'free_trial', 
      name: '🎁 Teste Grátis', 
      price: 'R$ 0 (7 dias)', 
      features: ['5 links', '2 plataformas', '2 conteúdos IA'],
      highlight: true
    },
    { id: 'starter', name: 'Starter', price: 'R$ 297/mês', features: ['50 links', '25 conteúdos IA', 'Suporte email'] },
    { id: 'pro', name: 'Pro', price: 'R$ 797/mês', features: ['500 links', '250 conteúdos IA', 'Suporte prioritário'] },
    { id: 'enterprise', name: 'Enterprise', price: 'R$ 997/mês', features: ['Links ilimitados', 'IA ilimitada', 'Suporte dedicado'] }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // Login
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });

        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          setIsAuthenticated(true);
          setUser(response.data.user);
          toast.success('Login realizado com sucesso!');
          
          // Verificar se usuário pagou
          const user = response.data.user;
          if (user.is_admin) {
            navigate('/admin');
          } else if (user.payment_status === 'paid' || user.payment_status === 'active') {
            navigate('/dashboard');
          } else {
            // Não pagou ainda - redirecionar para checkout
            toast.info('Complete seu pagamento para acessar o sistema!');
            navigate(`/checkout?plan=${user.subscription_plan || 'starter'}`);
          }
        }
      } else {
        // Registro - autentica mas vai para pagamento
        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          name: formData.name,
          plan: formData.plan
        });

        if (response.data.token) {
          // Salvar token e autenticar
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          localStorage.setItem('pending_payment', 'true');
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          
          // IMPORTANTE: Marcar como autenticado para poder acessar checkout
          setIsAuthenticated(true);
          setUser(response.data.user);
          
          // Verificar se é FREE TRIAL ou plano pago
          const newUser = response.data.user;
          if (newUser.subscription_plan === 'free_trial') {
            toast.success('🎉 Conta criada! Você tem 7 dias de teste grátis!');
            navigate('/dashboard');
          } else {
            toast.success('Conta criada! Agora finalize seu pagamento.');
            navigate(`/checkout?plan=${formData.plan}`);
          }
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Erro na autenticação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Formulário */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 w-full">
          {/* Logo */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold mb-2">
              <span className="text-white">💰 </span>
              <span className="text-yellow-400">Monetize</span>
              <span className="text-orange-500">Master</span>
            </h1>
            <p className="text-gray-400 text-lg">A ferramenta definitiva para afiliados</p>
            <p className="text-gray-500 mt-1">{isLogin ? 'Acesse sua conta' : 'Crie sua conta GRÁTIS'}</p>
            {!isLogin && (
              <div className="mt-3 bg-green-500/20 border border-green-500/50 rounded-lg p-2">
                <span className="text-green-400 text-sm font-semibold">🎁 7 dias de teste GRÁTIS!</span>
              </div>
            )}
          </div>

        {/* Toggle Login/Register */}
        <div className="flex mb-6 bg-white/5 rounded-lg p-1">
          <button
            onClick={() => setIsLogin(true)}
            className={`flex-1 py-2 rounded-lg font-semibold transition-all ${
              isLogin ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            Login
          </button>
          <button
            onClick={() => setIsLogin(false)}
            className={`flex-1 py-2 rounded-lg font-semibold transition-all ${
              !isLogin ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            Cadastrar
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-gray-300 text-sm mb-1">Nome Completo</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                placeholder="Seu nome"
                required={!isLogin}
              />
            </div>
          )}

          <div>
            <label className="block text-gray-300 text-sm mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
              placeholder="seu@email.com"
              required
            />
          </div>

          {!isLogin && (
            <div>
              <label className="block text-gray-300 text-sm mb-1">Telefone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                placeholder="(11) 99999-9999"
                required={!isLogin}
              />
            </div>
          )}

          <div>
            <label className="block text-gray-300 text-sm mb-1">Senha</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
              placeholder="••••••••"
              required
            />
          </div>

          {!isLogin && (
            <div>
              <label className="block text-gray-300 text-sm mb-2">Escolha seu Plano</label>
              <div className="space-y-2">
                {plans.map((plan) => (
                  <label
                    key={plan.id}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.plan === plan.id
                        ? plan.highlight 
                          ? 'border-green-500 bg-green-500/20' 
                          : 'border-yellow-500 bg-yellow-500/10'
                        : plan.highlight
                          ? 'border-green-500/50 bg-green-500/5 hover:border-green-500'
                          : 'border-white/20 bg-white/5 hover:border-white/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="plan"
                      value={plan.id}
                      checked={formData.plan === plan.id}
                      onChange={handleChange}
                      className="hidden"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className={`font-semibold ${plan.highlight ? 'text-green-400' : 'text-white'}`}>
                          {plan.name}
                        </span>
                        <span className={`font-bold ${plan.highlight ? 'text-green-400' : 'text-yellow-400'}`}>
                          {plan.price}
                        </span>
                      </div>
                      <div className="text-gray-400 text-xs mt-1">
                        {plan.features.join(' • ')}
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 ml-3 flex items-center justify-center ${
                      formData.plan === plan.id 
                        ? plan.highlight ? 'border-green-500' : 'border-yellow-500' 
                        : 'border-white/30'
                    }`}>
                      {formData.plan === plan.id && (
                        <div className={`w-3 h-3 rounded-full ${plan.highlight ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 text-white py-3 rounded-lg font-bold hover:from-yellow-600 hover:to-orange-700 transition-all disabled:opacity-50"
          >
            {loading ? 'Processando...' : isLogin ? 'ENTRAR' : 'CRIAR CONTA'}
          </button>
        </form>

        {isLogin && (
          <p className="text-center text-gray-400 text-sm mt-4">
            Esqueceu a senha?{' '}
            <a href="#" className="text-yellow-400 hover:underline">Recuperar</a>
          </p>
        )}

        <div className="mt-6 pt-6 border-t border-white/10 text-center">
          <a href="/" className="text-gray-400 hover:text-white text-sm">
            ← Voltar para a página inicial
          </a>
        </div>
      </div>
    </div>
    </div>
  );
};

export default LoginPage;
